import React from 'react';
import { useTranslation } from 'react-i18next';

import { Spotlight, type SpotlightCardItem, type SpotlightItem } from '../Spotlight';
import { LumioWaves } from './LumioWaves';
import type { ImportantNotice } from './useImportantNotices';
import {
  PremiumCtaLabel,
  PremiumEyebrow,
  PremiumPrice,
  PremiumTierPill,
  PremiumTitle,
  PremiumTitleRow,
} from './ImportantNow.styles';

/** Relative-age buckets for "purchased {when}". A stable id (never displayed or
 *  compared elsewhere) the component maps to an explicit, translatable phrase. */
type PurchaseAgeKind = 'just-now' | 'a-minute' | 'minutes' | 'an-hour' | 'hours' | 'a-day' | 'days';

/**
 * A relative-age bucket for "purchased {when}", pure (no i18n) so it stays
 * testable; the component resolves the returned `kind` to a translated phrase.
 * Singular/plural is split explicitly so it always reads naturally ("an hour ago",
 * not "1 hours ago").
 */
const purchaseAgeKind = (purchasedAtMs: number): { kind: PurchaseAgeKind; n?: number } => {
  const minutes = Math.floor((Date.now() - purchasedAtMs) / 60_000);
  if (minutes < 1) return { kind: 'just-now' };
  if (minutes < 60) return minutes === 1 ? { kind: 'a-minute' } : { kind: 'minutes', n: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? { kind: 'an-hour' } : { kind: 'hours', n: hours };
  const days = Math.floor(hours / 24);
  return days === 1 ? { kind: 'a-day' } : { kind: 'days', n: days };
};

export type ImportantNowProps = {
  notices: ImportantNotice[];
  /** Send the owner back into the configurator's payment step. */
  onRetryPayment: () => void;
  /** Send the owner into the customizer to finish their new website. */
  onContinueSetup: () => void;
  /** Resume the owner's half-finished, unpaid configurator session. */
  onContinueUnpaid: () => void;
  /** Cancel (discard) the owner's half-finished, unpaid configurator session. */
  onCancelUnpaid: () => void;
  /** Open Casino Settings and scroll to the deployment-status table (Lumio card). */
  onViewDeployment: () => void;
  /** Open the feature's product-detail page to finish paying its pending upgrade invoice. */
  onFinishFeaturePayment: (itemId: number) => void;
  /** "See articles" on the new-articles card: mark these ids new on the blog list, clear the
   *  notice, open the list. */
  onSeeNewArticles: (brandId: string, articleIds: string[]) => void;
  /** "Clear" on the new-articles card. */
  onClearNewArticles: (brandId: string) => void;
  /** "Top up" on the credits card: open the buy-credits sheet primed with what the next article
   *  needs. */
  onTopUpAutopilot: (brandId: string, creditsNeeded: number) => void;
  /** "Clear" on the credits card. */
  onClearAutopilotCredits: (brandId: string) => void;
  className?: string;
};

/**
 * The home "Important Now" notifications, drawn by Home's Spotlight (`../Spotlight`, the ows-ftux prototype's look:
 * Lumio's aurora under frosted glass, its card sizes, its pick). This maps each notice to its card; the data, words
 * and actions of every kind are the studio's own. Renders nothing when there are no notices. Every notice is the ONE
 * card shape, Lumio's pick's (the user, 2026-10-19: an eyebrow over the title, the line, the primary and a quiet
 * second); the tone (its glow over the card and the eyebrow's ink) encodes meaning: critical (red) → a failed payment,
 * action (amber) → finish your new casino, or Autopilot paused for credits, info (plain) → payment processing,
 * lumio (cyan/violet) → Lumio is generating the selected brand's content, or the blog has published articles of its
 * own, premium (gold) → a feature upgrade to pay. One verb makes the whole card the control; two verbs are real
 * buttons in a card that is not itself clickable. Lumio's pick (`ftux-pick`, FTUX - Getting Started) is the
 * Spotlight's pick, built by the FTUX itself (features/ftux/spotlight).
 */
export const ImportantNow = ({
  notices,
  onRetryPayment,
  onContinueSetup,
  onContinueUnpaid,
  onCancelUnpaid,
  onViewDeployment,
  onFinishFeaturePayment,
  onSeeNewArticles,
  onClearNewArticles,
  onTopUpAutopilot,
  onClearAutopilotCredits,
  className,
}: ImportantNowProps): React.ReactElement | null => {
  const { t } = useTranslation();

  // Resolve a relative-age bucket to its translated phrase. Each branch wraps an
  // explicit literal (so the extractor harvests it) and re-evaluates on a language
  // switch; the singular/plural split keeps every phrase reading naturally.
  const purchaseAgePhrase = (age: { kind: PurchaseAgeKind; n?: number }): string => {
    switch (age.kind) {
      case 'just-now':
        return t('just now');
      case 'a-minute':
        return t('a minute ago');
      case 'minutes':
        return t('{{n}} minutes ago', { n: age.n });
      case 'an-hour':
        return t('an hour ago');
      case 'hours':
        return t('{{n}} hours ago', { n: age.n });
      case 'a-day':
        return t('a day ago');
      case 'days':
        return t('{{n}} days ago', { n: age.n });
    }
  };

  const casinoSetupItem = (notice: ImportantNotice): SpotlightCardItem => {
    // Brand name (owner reached the naming step) reads best; otherwise lead with the
    // purchase age; otherwise a clean generic line for a brand-new instance.
    const age = notice.purchasedAt !== undefined ? purchaseAgeKind(notice.purchasedAt) : null;
    const when = age ? purchaseAgePhrase(age) : null;

    // The title is one line on the card (cut with an ellipsis): the whole of it in its tooltip, as the pick's.
    let title: React.ReactElement;
    let ariaLabel: string;
    if (notice.brandName) {
      ariaLabel = t('Finish setting up {{name}}', { name: notice.brandName });
      title = (
        <PremiumTitle
          text="Finish setting up {{name}}"
          placeholders={{ name: notice.brandName }}
          tag="span"
          title={ariaLabel}
        />
      );
    } else if (when) {
      ariaLabel = t('Finish setting up the website you purchased {{when}}', { when });
      title = (
        <PremiumTitle
          text="Finish setting up the website you purchased {{when}}"
          placeholders={{ when }}
          tag="span"
          title={ariaLabel}
        />
      );
    } else {
      ariaLabel = t('Finish setting up your new website');
      title = <PremiumTitle text="Finish setting up your new website" tag="span" title={ariaLabel} />;
    }

    return {
      id: notice.kind,
      tone: 'action',
      eyebrow: <PremiumEyebrow text="Your website" tag="span" $tone="action" />,
      title,
      body: { text: 'Your new website is ready to customize. Complete a few quick steps to take it live.' },
      action: { cta: <PremiumCtaLabel text="Continue" tag="span" />, ariaLabel, onActivate: onContinueSetup },
    };
  };

  // Two verbs (Continue, Cancel): real buttons, and the card is not itself clickable. When the
  // session cannot be cancelled it is still rendered this way, one real button, rather than
  // flipping the whole card into a control depending on data.
  const configuratorUnpaidItem = (notice: ImportantNotice): SpotlightCardItem => ({
    id: notice.kind,
    tone: 'action',
    eyebrow: <PremiumEyebrow text="Setup" tag="span" $tone="action" />,
    title: (
      <PremiumTitle text="Continue building your website" tag="span" title={t('Continue building your website')} />
    ),
    body: { text: "You started building a website but haven't completed checkout. Pick up right where you left off." },
    actions: {
      primary: { label: t('Continue'), onClick: onContinueUnpaid },
      secondary: notice.cancellable ? { label: t('Cancel'), onClick: onCancelUnpaid } : undefined,
    },
  });

  // The feature upgrade the owner began but never paid for: the feature and its price as the title,
  // one verb (finish paying on its product page).
  const featurePaymentItem = (notice: ImportantNotice): SpotlightCardItem | null => {
    const payment = notice.payment;
    if (!payment) return null;
    return {
      id: `feature-payment-${payment.invoiceId}`,
      tone: 'premium',
      eyebrow: <PremiumEyebrow text="Finish your upgrade" tag="span" $tone="premium" />,
      title: (
        <PremiumTitleRow>
          <PremiumTitle text="{{name}} for" placeholders={{ name: payment.name }} tag="span" title={payment.name} />
          <PremiumPrice>{payment.priceText}</PremiumPrice>
          {payment.tierName ? <PremiumTierPill text={payment.tierName} tag="span" /> : null}
        </PremiumTitleRow>
      ),
      body: { text: 'per month · complete the payment to switch it on.' },
      action: {
        cta: <PremiumCtaLabel text="Finish payment" tag="span" />,
        ariaLabel: t('Finish payment for {{name}}', { name: payment.name }),
        onActivate: () => onFinishFeaturePayment(payment.itemId),
      },
    };
  };

  /**
   * The blog published articles of its own (Autopilot, `docs/auto-generation.md` in spark-blog)
   * that the owner has not looked at. Lumio tone: it is the assistant's work, like the
   * content-generating card, but finished, so no waves. Two verbs (See articles, Clear): real
   * buttons, and the card is not itself clickable. Keyed by brand and kind, because the notice is
   * per brand and the strip may carry one brand's card while another's arrives.
   */
  const blogNewArticlesItem = (notice: ImportantNotice): SpotlightCardItem | null => {
    const info = notice.blogArticles;
    if (!info) return null;
    const n = info.ids.length;
    // Singular/plural split, like purchaseAgePhrase: `{{n}}`, never `{{count}}`, so i18next does not
    // go looking for plural-suffixed keys.
    return {
      id: `${info.brandId}:${notice.kind}`,
      tone: 'lumio',
      eyebrow: <PremiumEyebrow text="Blog" tag="span" $tone="lumio" />,
      title:
        n === 1 ? (
          <PremiumTitle text="1 new article" tag="span" title={t('1 new article')} />
        ) : (
          <PremiumTitle
            text="{{n}} new articles"
            placeholders={{ n }}
            tag="span"
            title={t('{{n}} new articles', { n })}
          />
        ),
      body: {
        text:
          n === 1
            ? 'Autopilot wrote and published it on your blog.'
            : 'Autopilot wrote and published them on your blog.',
      },
      actions: {
        primary: { label: t('See articles'), onClick: () => onSeeNewArticles(info.brandId, info.ids) },
        secondary: { label: t('Clear'), onClick: () => onClearNewArticles(info.brandId) },
      },
      testId: 'important-now-blog-new-articles',
    };
  };

  /**
   * Autopilot could not pay for its next article and is waiting for a top-up. Action tone: money
   * to spend before the blog carries on, the register of "finish your new casino". Two verbs (Top
   * up, Clear), real buttons. The card names the price the refused run needed, which is also what
   * the buy-credits sheet is primed with.
   */
  const blogCreditsItem = (notice: ImportantNotice): SpotlightCardItem | null => {
    const info = notice.blogCredits;
    if (!info) return null;
    const n = info.needed;
    return {
      id: `${info.brandId}:${notice.kind}`,
      tone: 'action',
      eyebrow: <PremiumEyebrow text="Blog" tag="span" $tone="action" />,
      title: <PremiumTitle text="Autopilot needs credits" tag="span" title={t('Autopilot needs credits')} />,
      body:
        n === 1
          ? { text: 'The next article needs 1 credit. Top up and it is written within minutes.' }
          : {
              text: 'The next article needs {{n}} credits. Top up and it is written within minutes.',
              placeholders: { n },
            },
      actions: {
        primary: { label: t('Top up'), onClick: () => onTopUpAutopilot(info.brandId, info.needed) },
        secondary: { label: t('Clear'), onClick: () => onClearAutopilotCredits(info.brandId) },
      },
      testId: 'important-now-blog-credits',
    };
  };

  const contentGeneratingItem = (notice: ImportantNotice): SpotlightCardItem => {
    const ariaLabel = notice.brandName
      ? t('View deployment status for {{name}}', { name: notice.brandName })
      : t('View deployment status');
    return {
      id: notice.kind,
      tone: 'lumio',
      backdrop: <LumioWaves />,
      eyebrow: <PremiumEyebrow text="Lumio" tag="span" $tone="lumio" />,
      title: notice.brandName ? (
        <PremiumTitle
          text="Lumio is generating content for {{name}}"
          placeholders={{ name: notice.brandName }}
          tag="span"
          title={t('Lumio is generating content for {{name}}', { name: notice.brandName })}
        />
      ) : (
        <PremiumTitle
          text="Lumio is generating your content"
          tag="span"
          title={t('Lumio is generating your content')}
        />
      ),
      body: {
        text: 'Promotions, banners, emails and marketing material. Lumio is crafting it all so your website launches ready to win.',
      },
      action: { cta: <PremiumCtaLabel text="View status" tag="span" />, ariaLabel, onActivate: onViewDeployment },
    };
  };

  const itemOf = (notice: ImportantNotice): SpotlightItem | null => {
    switch (notice.kind) {
      case 'payment-failed':
        // One verb, so the whole card retries; the eyebrow keeps the tone's register.
        return {
          id: notice.kind,
          tone: 'critical',
          eyebrow: <PremiumEyebrow text="Action needed" tag="span" $tone="critical" />,
          title: <PremiumTitle text="Payment failed" tag="span" title={t('Payment failed')} />,
          body: {
            text: "We couldn't process your last payment. Your casino service will be discontinued if payment isn't completed.",
          },
          action: {
            cta: <PremiumCtaLabel text="Retry payment" tag="span" />,
            ariaLabel: t('Retry payment'),
            onActivate: onRetryPayment,
          },
        };

      case 'payment-processing':
        // Nothing to do, so no button row and no click.
        return {
          id: notice.kind,
          tone: 'info',
          eyebrow: <PremiumEyebrow text="In progress" tag="span" $tone="info" />,
          title: <PremiumTitle text="Payment processing" tag="span" title={t('Payment processing')} />,
          body: { text: "We're confirming your payment. This can take a few minutes, no action needed." },
        };

      case 'content-generating':
        return contentGeneratingItem(notice);

      case 'casino-setup':
        return casinoSetupItem(notice);

      case 'configurator-unpaid':
        return configuratorUnpaidItem(notice);

      case 'feature-payment':
        return featurePaymentItem(notice);

      case 'blog-new-articles':
        return blogNewArticlesItem(notice);

      case 'blog-credits':
        return blogCreditsItem(notice);

      case 'ftux-pick':
        // Lumio's pick (FTUX - Getting Started): the waiting piece's card, built by the FTUX (useFtuxSpotlight).
        return notice.ftuxPick ?? null;

      default:
        return null;
    }
  };

  const items = notices.flatMap((notice) => {
    const item = itemOf(notice);
    return item ? [item] : [];
  });
  return <Spotlight items={items} className={className} />;
};

export default ImportantNow;
